import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { SignatureField, SignatureFieldHandle, VerticalBarLoader } from '@roc/ui';
import { observer } from 'mobx-react';
import { useRef } from 'react';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    maxWidth: 900,
    margin: 'auto',
    padding: theme.spacing(4),
  },
  title: {
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  subTitle: {
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  paragraph: {
    fontSize: '0.8rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(2),
  },
  acknowledgements: {
    fontSize: '0.8rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
  },
  submitButton: {
    color: '#fff',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(4),
    width: '25%',
  },
}));

export const LenderBankInfoSignatureStep = observer(({ store }) => {
  const classes = useStyles();
  const signatureRef = useRef<SignatureFieldHandle>(null);
  const values = store.verifyBankInformationDocusignFormStore.getFormValues();

  const handleSubmit = () => {
    if (!signatureRef.current.isSigned()) {
      store.globalStore.notificationStore.showErrorNotification({
        message: 'Please sign the document before submitting it',
      });
      return;
    }
    store.submitVerifyBankInfo(signatureRef.current.getSignature());
  };

  return (
    <Box className={classes.container}>
      {store.loading && <VerticalBarLoader />}

      <Typography variant="h5" className={classes.title}>
        STANDING AUTHORIZATION AND WIRE INSTRUCTIONS
      </Typography>
      <Typography variant="h6" className={classes.subTitle}>
        LENDER POINTS ADVANCE
      </Typography>

      <Typography className={classes.paragraph}>
        Effective as of today, on behalf of the undersigned entity, I hereby authorize the wire
        transfer and deposit of funds in connection with above referenced Loan by Loan Funder LLC
        (Lender), or any of its parent companies, affiliates, authorized servicers, subservicers,
        agents, representatives, or permitted successors or assigns (Lender Affiliates and Agents),
        into the following account:
      </Typography>

      <Typography className={classes.paragraph}>
        Account Name/Title: <strong>{values.accountName}</strong>
        <br />
        ABA (FedWire) Routing Number: <strong>{values.routingNumber}</strong>
        <br />
        Account Number: <strong>{values.accountNumber}</strong>
        <br />
        Bank Name: <strong>{values.bankName}</strong>
      </Typography>

      <Typography className={classes.paragraph}>
        If the above account is not the undersigned entity's account, or if the above account has an
        account name/title that is not the same as the name of the undersigned entity, on behalf of
        the undersigned entity, I hereby further expressly authorize the wire transfer, and deposit
        of funds, in connection with the Loan into such account by Lender or any Lender Agent, and I
        hereby release Lender and all Lender Agents, and each of their respective officers, managers,
        directors, consultants, agents, advisors, and representatives, from all loss, damage,
        liability, cost, or expense arising out of or resulting from the deposit of funds into such
        account.
      </Typography>

      <Typography className={classes.paragraph}>
        The foregoing authorization and wire instructions shall be standing and shall remain in full
        force and effect until such time as they are revoked or amended in writing by the
        undersigned. This Standing Authorization and Wire Instructions expressly revokes all prior
        Standing Authorizations and Wire Instructions (Construction Draws/Loan Advances) given on
        behalf of the undersigned entity in connection with the above referenced Loan. All such prior
        authorizations and wire instructions are void and of no further force or effect as of the
        above referenced Effective Date.
      </Typography>

      <Typography className={classes.paragraph}>
        On behalf of the undersigned entity, I hereby acknowledge, represent, and/or warrant as
        follows:
      </Typography>

      <ul className={classes.acknowledgements}>
        <li>
          I am a duly authorized representative of the undersigned entity and have been duly
          authorized by such entity to execute this Standing Authorization and Wire Instructions on
          behalf of such entity.
        </li>
        <li>
          The above wire and account information is accurate and correct and I acknowledge
          responsibility for any errors resulting from incorrect/inaccurate information provided.
        </li>
        <li>
          I authorize Lender and any Lender Agent to effect wires/transfers/deposits into the above
          specified account in connection with the Loan in accordance with the terms and provisions
          of the loan documents related to the Loan.
        </li>
        <li>
          Lender reserves the right, on behalf of itself and any Lender Agent, to require additional
          verification of the above specified account information, whether written or oral, prior to
          authorizing a wire/transfer/deposit, and such additional verification may delay the
          execution of a wire.
        </li>
        <li>
          The undersigned entity (1) releases Lender and all Lender Agents, and each of their
          respective officers, managers, directors, consultants, agents, advisors, and
          representatives, from all loss, damage, liability, cost, or expense (Loss) arising out of or
          resulting from any action taken by Lender or a Lender Agent upon instructions provided
          under this Standing Authorization and Wire Instructions, unless the Loss arose out of
          Lender's or a Lender Agent's failure to exercise ordinary care, act in good faith, or act
          in accordance with the wire instructions given hereunder, and (2) agrees to indemnify and
          hold Lender and all Lender Agents harmless from any Loss arising out of or resulting from
          any action taken by Lender or a Lender Agent in good faith upon instructions provided under
          this Standing Authorization and Wire Instructions.
        </li>
        <li>
          Lender shall not be liable for refusing to execute a wire transfer instruction based on
          Lender's reasonable belief that such wire is violative of applicable law or could cause
          Lender or a Lender Agent to incur legal liability for following such instructions.
        </li>
        <li>
          Lender cannot guarantee the length of time it takes for funds to be credited to the above
          specified account after a wire is initiated.
        </li>
        <li>
          Neither Lender nor any Lender Agent has any influence or responsibility for fees or
          surcharges imposed by other financial institutions involved in the transfer of funds.
        </li>
      </ul>

      <Typography className={classes.paragraph}>
        <strong>
          THIS STANDING AUTHORIZATION AND WIRE INSTRUCTIONS SHALL BE GOVERNED BY NEW YORK LAW.
        </strong>
      </Typography>

      <Typography className={classes.paragraph}>
        Full Name(s): <strong>{values.fullName}</strong>
        <br />
        Phone Number(s): <strong>{values.phoneNumber}</strong>
        <br />
        Email Address(es): <strong>{values.emailAddress}</strong>
        <br />
        Street Name: <strong>{values.streetName}</strong>
        <br />
        City: <strong>{values.city}</strong>
        <br />
        Zip Code: <strong>{values.zipCode}</strong>
        <br />
        State: <strong>{values.state}</strong>
      </Typography>

      <SignatureField ref={signatureRef} penColor="green" />

      <Box display="flex" justifyContent="center">
        <Button
          className={classes.submitButton}
          variant="contained"
          onClick={handleSubmit}
          disabled={store.loading}
        >
          Sign &amp; Submit
        </Button>
      </Box>
    </Box>
  );
});
